import { Button } from '@zep-us/zus/button';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { CheckSmallCircleFillIcon, XIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
type VoucherToastViewProps = {
  actionLabel: string;
  className?: string;
  closeButtonLabel: string;
  /** 없으면 제목 한 줄짜리 토스트가 된다 (시안 28913:51596, 360×60). */
  description?: string;
  onAction: () => void;
  onClose: () => void;
  title: string;
};

/**
 * 이용권 결과 토스트의 표시 전용 뷰.
 *
 * 두 시안이 이 컴포넌트 하나를 공유한다 — 컨테이너 스펙이 완전히 같고 설명 유무만 다르다.
 * - 적용 완료: 28913:51593, 360×66 (설명 있음)
 * - 보관 완료: 28913:51596, 360×60 (설명 없음)
 *
 * ZUS `Toast` 를 그대로 쓰지 않는 이유: 이 시안들은 ZUS Toast 마스터(8908:60)와
 * 세 군데가 어긋난다 — 세로 중앙 정렬, 액션 버튼 sm(36px), 우상단 절대배치 닫기 버튼.
 * 셋 다 ZUS Toast 내부 구조라 className 으로는 못 바꾸고, 반대로 ZUS 를 이 시안에
 * 맞추면 전 앱의 토스트가 디자인시스템 마스터에서 이탈한다. 그래서 이 화면 전용으로만 조립한다.
 *
 * 기본 폭은 360px이고, 내용이 길면 Sonner의 360px 래퍼를 기준으로
 * 양쪽으로 최대 450px까지 확장한다.
 * 좁은 모바일에서는 좌우 16px 여백을 남기고 줄어든다.
 */
export const VoucherToastView = ({
  actionLabel,
  className,
  closeButtonLabel,
  description,
  onAction,
  onClose,
  title
}: VoucherToastViewProps) => <div role="status" aria-live="polite" className={cn('relative left-1/2 flex w-max min-w-[min(360px,calc(100vw-32px))] max-w-[min(450px,calc(100vw-32px))] -translate-x-1/2 items-center gap-lg', 'rounded-sm border border-alternative bg-opacity-white-10 py-sm pl-lg pr-xl', 'shadow-lg backdrop-blur-[2px]', className)} data-sentry-component="VoucherToastView" data-sentry-source-file="VoucherToastView.tsx">
    <div className="flex min-w-0 flex-1 items-center gap-sm">
      {/* 시안은 icon-fill/semi-strong(#15B09C) — zusToast.success 기본값인 positive 가 아니다. */}
      <CheckSmallCircleFillIcon color={ICON_COLOR.semistrong} size="md" data-sentry-element="CheckSmallCircleFillIcon" data-sentry-source-file="VoucherToastView.tsx" />
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-4xs break-words break-keep">
        <p className="w-full text-body-lg-semibold text-default">{title}</p>
        {/* 시안은 설명도 text/default 다 — ZUS Toast 마스터의 text/alternative 와 다르다. */}
        {description && <p className="w-full text-caption-lg-medium text-default">
            {description}
          </p>}
      </div>
    </div>

    <Button type="button" variant="outlined" intent="neutral" size="sm" className="shrink-0" onClick={onAction} data-sentry-element="Button" data-sentry-source-file="VoucherToastView.tsx">
      {actionLabel}
    </Button>

    <Button type="button" variant="transparent" intent="neutral" size="2xs" aria-label={closeButtonLabel} className="absolute right-[4px] top-[4px] p-0" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="VoucherToastView.tsx">
      <XIcon size="xs" data-sentry-element="XIcon" data-sentry-source-file="VoucherToastView.tsx" />
    </Button>
  </div>;